import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  IconButton,
  Tooltip,
  useBreakpointValue,
  ButtonGroup,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Center,
  SimpleGrid,
  Circle,
  Icon,
} from '@chakra-ui/react';
import {
  FiCheck,
  FiX,
  FiInfo,
  FiSearch,
  FiCalendar,
  FiClock,
  FiMail,
  FiPhone,
  FiEdit3,
  FiExternalLink,
} from 'react-icons/fi';

// URL del perfil del alumno (WP ID)
const studentProfileUrl = (wpUserId) => {
  if (!wpUserId) return null;
  return `${window.location.href.split('#')[0]}#/admin/usuario/${wpUserId}`;
};

import {
  getTeacherAppointments,
  updateAppointmentStatus,
  cancelAppointment
} from 'services/appointmentBooking';

// Estados de citas — mismos tokens Datofy V1 que EventModal.js (APPT_STATUS)
const APPOINTMENT_STATUSES = {
  pending:   { label: 'Pendiente',  bg: 'warn.bg',   color: 'warn.500'   },
  confirmed: { label: 'Confirmada', bg: 'sky.bg',    color: 'sky.ink'    },
  completed: { label: 'Completada', bg: 'ok.bg',     color: 'ok.500'     },
  cancelled: { label: 'Cancelada',  bg: 'danger.bg', color: 'danger.500' },
};

// Pill reutilizable estilo Datofy V1
const Pill = ({ bg, color, children, ...rest }) => (
  <Box
    as="span"
    display="inline-flex"
    alignItems="center"
    gap="4px"
    px="9px"
    py="3px"
    borderRadius="999px"
    bg={bg}
    color={color}
    fontSize="11.5px"
    fontWeight="700"
    {...rest}
  >
    {children}
  </Box>
);

function AppointmentsList({ teacherId, currentUser }) {
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [modalAction, setModalAction] = useState(''); // 'confirm', 'complete', 'cancel'
  const [notes, setNotes] = useState('');
  const [cancelReason, setCancelReason] = useState('');

  // Filtros
  const [filters, setFilters] = useState({
    status: '',
    date: '',
    search: ''
  });

  const isMobile = useBreakpointValue({ base: true, md: false });

  const toast = useToast();

  // Pestaña activa: 'upcoming' | 'past' | 'all'
  const [activeTab, setActiveTab] = useState('upcoming');

  // Cargar citas — sin filtro de fechas para traer todas
  const loadAppointments = async () => {
    setLoading(true);
    try {
      const response = await getTeacherAppointments(teacherId);

      if (response.success) {
        const sortedAppointments = (response.data || []).sort((a, b) => {
          const dateA = new Date(`${a.appointment_date}T${a.start_time}`);
          const dateB = new Date(`${b.appointment_date}T${b.start_time}`);
          return dateA - dateB;
        });
        setAppointments(sortedAppointments);
      } else {
        throw new Error(response.message || 'Error al cargar citas');
      }
    } catch (error) {
      console.error('Error loading appointments:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudieron cargar las citas',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Efecto para cargar citas
  useEffect(() => {
    if (teacherId) {
      loadAppointments();
    }
  }, [teacherId]);

  // Filtrar citas
  useEffect(() => {
    const now = new Date();
    let filtered = [...appointments];
    const hasSearch = !!filters.search.trim();

    // Filtro por pestaña (próximas / pasadas / todas) — se omite mientras se busca,
    // así una cita pasada también aparece al buscarla por código, nombre, etc.
    if (!hasSearch) {
      if (activeTab === 'upcoming') {
        filtered = filtered.filter(apt => new Date(`${apt.appointment_date}T${apt.start_time}`) >= now);
      } else if (activeTab === 'past') {
        filtered = filtered
          .filter(apt => new Date(`${apt.appointment_date}T${apt.start_time}`) < now)
          .reverse(); // más recientes primero en pasadas
      }
    }

    // Filtro por estado
    if (filters.status) {
      filtered = filtered.filter(apt => apt.status === filters.status);
    }

    // Filtro por fecha
    if (filters.date) {
      filtered = filtered.filter(apt => apt.appointment_date === filters.date);
    }

    // Filtro por búsqueda (nombre estudiante, apoderado, notas o código de confirmación)
    if (hasSearch) {
      const search = filters.search.toLowerCase().replace(/^#/, '');
      filtered = filtered.filter(apt =>
        apt.student_name?.toLowerCase().includes(search) ||
        apt.guardian_name?.toLowerCase().includes(search) ||
        apt.guardian_email?.toLowerCase().includes(search) ||
        apt.notes?.toLowerCase().includes(search) ||
        apt.confirmation_code?.toLowerCase().includes(search)
      );
    }

    setFilteredAppointments(filtered);
  }, [appointments, filters, activeTab]);

  // Formatear fecha y hora
  const formatDateTime = (date, time) => {
    const dateTime = new Date(`${date}T${time}`);
    return {
      date: dateTime.toLocaleDateString('es-CL', {
        weekday: 'short',
        day: 'numeric',
        month: 'short'
      }),
      time: dateTime.toLocaleTimeString('es-CL', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
    };
  };

  // Verificar si una cita es hoy
  const isToday = (appointmentDate) => {
    const today = new Date().toISOString().split('T')[0];
    return appointmentDate === today;
  };

  // Verificar si una cita ya pasó
  const isPast = (appointmentDate, startTime) => {
    const appointmentDateTime = new Date(`${appointmentDate}T${startTime}`);
    return appointmentDateTime < new Date();
  };

  // Formatear fecha de creación/registro
  const formatCreatedAt = (createdAt) => {
    if (!createdAt) return '—';
    const d = new Date(createdAt);
    if (isNaN(d)) return createdAt;
    return d.toLocaleString('es-CL', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false
    });
  };

  // Abrir modal de detalle (solo lectura)
  const openDetailModal = (appointment) => {
    setSelectedAppointment(appointment);
    setModalAction('view');
    setNotes('');
    setCancelReason('');
    setShowModal(true);
  };

  // Abrir modal de acciones
  const openActionModal = (appointment, action) => {
    setSelectedAppointment(appointment);
    setModalAction(action);
    setNotes(appointment.teacher_notes || '');
    setCancelReason('');
    setShowModal(true);
  };

  // Cerrar modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedAppointment(null);
    setModalAction('');
    setNotes('');
    setCancelReason('');
  };

  // Ejecutar acción
  const executeAction = async () => {
    if (!selectedAppointment) return;

    try {
      setLoading(true);
      let response;

      switch (modalAction) {
        case 'confirm':
          response = await updateAppointmentStatus(
            selectedAppointment.id,
            'confirmed',
            notes
          );
          break;

        case 'complete':
          response = await updateAppointmentStatus(
            selectedAppointment.id,
            'completed',
            notes
          );
          break;

        case 'cancel':
          response = await cancelAppointment(
            selectedAppointment.id,
            cancelReason
          );
          break;

        default:
          throw new Error('Acción no válida');
      }

      if (response.success) {
        toast({
          title: 'Éxito',
          description: `Cita ${modalAction === 'confirm' ? 'confirmada' : modalAction === 'complete' ? 'completada' : 'cancelada'} correctamente`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        closeModal();
        loadAppointments();
      }
    } catch (error) {
      console.error('Error executing action:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo realizar la acción',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Renderizar vista móvil
  const renderMobileView = () => (
    <VStack spacing={3} align="stretch">
      {filteredAppointments.map(appointment => {
        const formatted = formatDateTime(appointment.appointment_date, appointment.start_time);
        const status = APPOINTMENT_STATUSES[appointment.status] || APPOINTMENT_STATUSES.pending;
        const isAppointmentToday = isToday(appointment.appointment_date);
        const isAppointmentPast = isPast(appointment.appointment_date, appointment.start_time);

        return (
          <Box key={appointment.id} bg="white" border="1px solid" borderColor="cream.line" borderRadius="14px"
            boxShadow="0 1px 2px rgba(54,38,22,.05)">
            <Box p={4}>
              <VStack spacing={3} align="stretch">
                {/* Header */}
                <Flex justify="space-between" align="start">
                  <VStack spacing={1} align="start">
                    <HStack spacing={1}>
                      <Text fontWeight="700" fontSize="md" color="ink.900">
                        {appointment.student_name}
                      </Text>
                      {appointment.student_id && (
                        <Tooltip label="Ver perfil del alumno">
                          <IconButton
                            as="a"
                            href={studentProfileUrl(appointment.student_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            icon={<Icon as={FiExternalLink} />}
                            size="xs"
                            variant="ghost"
                            color="coral.600"
                            _hover={{ bg: 'coral.tint' }}
                            aria-label="Ver perfil"
                          />
                        </Tooltip>
                      )}
                    </HStack>
                    {appointment.student_rut && (
                      <Text fontSize="xs" color="ink.400">RUT: {appointment.student_rut}</Text>
                    )}
                    <HStack spacing={2}>
                      <Pill bg={status.bg} color={status.color}>{status.label}</Pill>
                      {isAppointmentToday && (
                        <Pill bg="sky.bg" color="sky.ink">Hoy</Pill>
                      )}
                    </HStack>
                  </VStack>

                  <Text fontSize="sm" color="ink.400">
                    #{appointment.id}
                  </Text>
                </Flex>

                <Box borderTop="1px solid" borderColor="cream.line" />

                {/* Detalles */}
                <VStack spacing={2} align="start" fontSize="sm">
                  {appointment.subject && (
                    <HStack spacing={2} align="start">
                      <Icon as={FiInfo} boxSize="14px" color="sky.ink" mt="2px" flexShrink={0} />
                      <Text fontWeight="600" color="sky.ink">{appointment.subject}</Text>
                    </HStack>
                  )}

                  <HStack spacing={2}>
                    <Icon as={FiCalendar} boxSize="14px" color="ink.400" />
                    <Text color="ink.900">{formatted.date}</Text>
                  </HStack>

                  <HStack spacing={2}>
                    <Icon as={FiClock} boxSize="14px" color="ink.400" />
                    <Text color="ink.900">{formatted.time}</Text>
                  </HStack>

                  <HStack spacing={2}>
                    <Icon as={FiMail} boxSize="14px" color="ink.400" />
                    <Text color="ink.900">{appointment.guardian_name}</Text>
                  </HStack>

                  {appointment.guardian_phone && (
                    <HStack spacing={2}>
                      <Icon as={FiPhone} boxSize="14px" color="ink.400" />
                      <Text color="ink.900">{appointment.guardian_phone}</Text>
                    </HStack>
                  )}
                </VStack>

                {/* Notas */}
                {appointment.notes && (
                  <Box>
                    <Text fontSize="xs" color="ink.400" mb={1}>Notas:</Text>
                    <Text fontSize="sm" bg="cream.bg2" color="ink.900" p={2} borderRadius="9px">
                      {appointment.notes}
                    </Text>
                  </Box>
                )}

                {/* Registrada */}
                <HStack spacing={2}>
                  <Icon as={FiClock} boxSize="11px" color="ink.400" />
                  <Text fontSize="xs" color="ink.400">
                    Registrada: {formatCreatedAt(appointment.created_at)}
                  </Text>
                </HStack>

                {/* Acciones */}
                <ButtonGroup size="sm" spacing={2} flexWrap="wrap">
                  <Button
                    variant="outline"
                    borderColor="cream.line2"
                    color="ink.700"
                    leftIcon={<Icon as={FiInfo} />}
                    onClick={() => openDetailModal(appointment)}
                  >
                    Ver detalle
                  </Button>
                  {appointment.status === 'pending' && !isAppointmentPast && (
                    <Button
                      bg="sky.ink"
                      color="white"
                      _hover={{ opacity: 0.9 }}
                      leftIcon={<Icon as={FiCheck} />}
                      onClick={() => openActionModal(appointment, 'confirm')}
                    >
                      Confirmar
                    </Button>
                  )}
                  {appointment.status === 'confirmed' && !isAppointmentPast && (
                    <Button
                      bg="ok.500"
                      color="white"
                      _hover={{ opacity: 0.9 }}
                      leftIcon={<Icon as={FiCheck} />}
                      onClick={() => openActionModal(appointment, 'complete')}
                    >
                      Completar
                    </Button>
                  )}
                  {appointment.status !== 'cancelled' && (
                    <Button
                      variant="outline"
                      borderColor="danger.500"
                      color="danger.500"
                      _hover={{ bg: 'danger.bg' }}
                      leftIcon={<Icon as={FiX} />}
                      onClick={() => openActionModal(appointment, 'cancel')}
                    >
                      Cancelar
                    </Button>
                  )}
                </ButtonGroup>
              </VStack>
            </Box>
          </Box>
        );
      })}
    </VStack>
  );

  // Renderizar vista de escritorio
  const renderDesktopView = () => (
    <Table size="sm">
      <Thead>
        <Tr>
          <Th color="ink.400">Estudiante</Th>
          <Th color="ink.400">Asunto</Th>
          <Th color="ink.400">Fecha / Hora</Th>
          <Th color="ink.400">Apoderado</Th>
          <Th color="ink.400">Estado</Th>
          <Th color="ink.400">Registrada</Th>
          <Th color="ink.400">Acciones</Th>
        </Tr>
      </Thead>
      <Tbody>
        {filteredAppointments.map(appointment => {
          const formatted = formatDateTime(appointment.appointment_date, appointment.start_time);
          const status = APPOINTMENT_STATUSES[appointment.status] || APPOINTMENT_STATUSES.pending;
          const isAppointmentToday = isToday(appointment.appointment_date);
          const isAppointmentPast = isPast(appointment.appointment_date, appointment.start_time);

          return (
            <Tr key={appointment.id} opacity={isAppointmentPast ? 0.75 : 1}
              _hover={{ bg: 'cream.bg2', cursor: 'pointer' }}
              onClick={() => openDetailModal(appointment)}>
              <Td onClick={e => e.stopPropagation()}>
                <VStack spacing={1} align="start">
                  <HStack spacing={1}>
                    <Text fontWeight="600" fontSize="sm" color="ink.900">{appointment.student_name}</Text>
                    {appointment.student_id && (
                      <Tooltip label="Ver perfil del alumno">
                        <IconButton
                          as="a"
                          href={studentProfileUrl(appointment.student_id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          icon={<Icon as={FiExternalLink} />}
                          size="xs"
                          variant="ghost"
                          color="coral.600"
                          _hover={{ bg: 'coral.tint' }}
                          aria-label="Ver perfil"
                        />
                      </Tooltip>
                    )}
                  </HStack>
                  {appointment.student_rut && (
                    <Text fontSize="xs" color="ink.400">RUT: {appointment.student_rut}</Text>
                  )}
                </VStack>
              </Td>

              <Td>
                <Text fontSize="sm" maxW="150px" noOfLines={2} color={appointment.subject ? 'ink.900' : 'ink.400'} fontStyle={appointment.subject ? 'normal' : 'italic'}>
                  {appointment.subject || '—'}
                </Text>
              </Td>

              <Td>
                <VStack spacing={0} align="start">
                  <HStack spacing={1}>
                    <Text fontSize="sm" color="ink.900">{formatted.date}</Text>
                    {isAppointmentToday && (
                      <Pill bg="sky.bg" color="sky.ink">Hoy</Pill>
                    )}
                  </HStack>
                  <Text fontSize="xs" color="ink.400">{formatted.time}{appointment.end_time ? ` – ${appointment.end_time.slice(0,5)}` : ''}</Text>
                </VStack>
              </Td>

              <Td>
                <VStack spacing={0} align="start">
                  <Text fontSize="sm" color="ink.900">{appointment.guardian_name}</Text>
                  <Text fontSize="xs" color="ink.400" noOfLines={1}>{appointment.guardian_email}</Text>
                  {appointment.guardian_phone && (
                    <Text fontSize="xs" color="ink.400">{appointment.guardian_phone}</Text>
                  )}
                </VStack>
              </Td>

              <Td>
                <Pill bg={status.bg} color={status.color}>{status.label}</Pill>
              </Td>

              <Td>
                <Text fontSize="xs" color="ink.400" whiteSpace="nowrap">
                  {formatCreatedAt(appointment.created_at)}
                </Text>
              </Td>

              <Td onClick={e => e.stopPropagation()}>
                <ButtonGroup size="xs" spacing={1}>
                  <Tooltip label="Ver detalle completo">
                    <IconButton
                      icon={<Icon as={FiInfo} />}
                      variant="ghost"
                      color="coral.600"
                      _hover={{ bg: 'coral.tint' }}
                      aria-label="Ver detalle"
                      onClick={() => openDetailModal(appointment)}
                    />
                  </Tooltip>
                  {appointment.status === 'pending' && !isAppointmentPast && (
                    <Tooltip label="Confirmar cita">
                      <IconButton
                        icon={<Icon as={FiCheck} />}
                        variant="outline"
                        borderColor="sky.ink"
                        color="sky.ink"
                        _hover={{ bg: 'sky.bg' }}
                        aria-label="Confirmar"
                        onClick={() => openActionModal(appointment, 'confirm')}
                      />
                    </Tooltip>
                  )}
                  {appointment.status === 'confirmed' && !isAppointmentPast && (
                    <Tooltip label="Marcar como completada">
                      <IconButton
                        icon={<Icon as={FiCheck} />}
                        variant="outline"
                        borderColor="ok.500"
                        color="ok.500"
                        _hover={{ bg: 'ok.bg' }}
                        aria-label="Completar"
                        onClick={() => openActionModal(appointment, 'complete')}
                      />
                    </Tooltip>
                  )}
                  {appointment.status !== 'cancelled' && (
                    <Tooltip label="Cancelar cita">
                      <IconButton
                        icon={<Icon as={FiX} />}
                        variant="outline"
                        borderColor="danger.500"
                        color="danger.500"
                        _hover={{ bg: 'danger.bg' }}
                        aria-label="Cancelar"
                        onClick={() => openActionModal(appointment, 'cancel')}
                      />
                    </Tooltip>
                  )}
                </ButtonGroup>
              </Td>
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );

  return (
    <VStack spacing={6} align="stretch" fontFamily="jakarta">

      {/* Header con filtros */}
      <Box bg="white" border="1px solid" borderColor="cream.line" boxShadow="0 4px 6px -1px rgba(43,39,35,0.06)" borderRadius="18px">
        <Box px={5} pt={5} pb={2}>
          <VStack spacing={4}>
            <Flex justify="space-between" align="center" w="full">
              <Text fontSize="xl" fontWeight="800" color="ink.900">
                Mis Citas Agendadas
              </Text>
              <Button
                size="sm"
                variant="outline"
                borderColor="cream.line2"
                color="ink.700"
                onClick={loadAppointments}
                isLoading={loading}
                leftIcon={<Icon as={FiCalendar} />}
              >
                Actualizar
              </Button>
            </Flex>

            {/* Tabs próximas / pasadas / todas */}
            <ButtonGroup size="sm" isAttached w="full">
              {[
                { key: 'upcoming', label: `Próximas (${appointments.filter(a => new Date(`${a.appointment_date}T${a.start_time}`) >= new Date()).length})` },
                { key: 'past',     label: `Pasadas (${appointments.filter(a => new Date(`${a.appointment_date}T${a.start_time}`) < new Date()).length})` },
                { key: 'all',      label: `Todas (${appointments.length})` },
              ].map(tab => (
                <Button
                  key={tab.key}
                  flex="1"
                  variant={activeTab === tab.key ? 'solid' : 'outline'}
                  bg={activeTab === tab.key ? 'coral.500' : 'transparent'}
                  color={activeTab === tab.key ? 'white' : 'ink.700'}
                  borderColor="cream.line2"
                  _hover={activeTab === tab.key ? { bg: 'coral.600' } : { bg: 'cream.bg2' }}
                  onClick={() => setActiveTab(tab.key)}
                  fontSize="xs"
                >
                  {tab.label}
                </Button>
              ))}
            </ButtonGroup>

            {/* Filtros */}
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} w="full">
              <FormControl>
                <InputGroup size="sm">
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FiSearch} color="ink.400" boxSize="14px" />
                  </InputLeftElement>
                  <Input
                    placeholder="Buscar estudiante, apoderado o código..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    focusBorderColor="coral.500"
                  />
                  {filters.search && (
                    <InputRightElement>
                      <IconButton icon={<Icon as={FiX} boxSize="11px" />} size="xs" variant="ghost" color="ink.400"
                        onClick={() => setFilters(prev => ({ ...prev, search: '' }))} aria-label="Limpiar" />
                    </InputRightElement>
                  )}
                </InputGroup>
              </FormControl>

              <FormControl>
                <Select
                  size="sm"
                  placeholder="Todos los estados"
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  focusBorderColor="coral.500"
                >
                  {Object.entries(APPOINTMENT_STATUSES).map(([key, status]) => (
                    <option key={key} value={key}>{status.label}</option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <Input
                  type="date"
                  size="sm"
                  value={filters.date}
                  onChange={(e) => setFilters(prev => ({ ...prev, date: e.target.value }))}
                  focusBorderColor="coral.500"
                />
              </FormControl>
            </SimpleGrid>
          </VStack>
        </Box>
      </Box>

      {/* Contenido */}
      <Box bg="white" border="1px solid" borderColor="cream.line" boxShadow="0 4px 6px -1px rgba(43,39,35,0.06)" borderRadius="18px">
        <Box p={5}>
          {loading ? (
            <Center py={8}>
              <VStack spacing={3}>
                <Spinner size="lg" color="coral.500" />
                <Text color="ink.400" fontSize="sm">
                  Cargando citas...
                </Text>
              </VStack>
            </Center>
          ) : filteredAppointments.length === 0 ? (
            <HStack p={4} bg="sky.bg" borderRadius="14px" spacing={3}>
              <Icon as={FiInfo} color="sky.ink" boxSize="16px" />
              <Text fontSize="sm" color="sky.ink">
                {appointments.length === 0
                  ? 'No tiene citas agendadas aún'
                  : activeTab === 'upcoming'
                    ? 'No hay citas próximas. Revise la pestaña "Pasadas" o "Todas".'
                    : activeTab === 'past'
                      ? 'No hay citas pasadas.'
                      : 'No se encontraron citas con los filtros aplicados.'
                }
              </Text>
            </HStack>
          ) : (
            <>
              <Text fontSize="sm" color="ink.400" mb={4}>
                Mostrando {filteredAppointments.length} de {appointments.length} citas
              </Text>

              {isMobile ? renderMobileView() : renderDesktopView()}
            </>
          )}
        </Box>
      </Box>

      {/* Modal de acciones / detalle */}
      <Modal isOpen={showModal} onClose={closeModal} size="lg" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent bg="cream.bg" borderRadius="18px" fontFamily="jakarta">
          <ModalHeader fontSize="md" color="ink.900" fontWeight="800" borderBottom="1px solid" borderColor="cream.line">
            {modalAction === 'confirm' && 'Confirmar Cita'}
            {modalAction === 'complete' && 'Completar Cita'}
            {modalAction === 'cancel' && 'Cancelar Cita'}
            {modalAction === 'view' && (selectedAppointment ? `Cita #${selectedAppointment.id}` : 'Detalle de Cita')}
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            {selectedAppointment && (() => {
              const apt = selectedAppointment;
              const apptStatus = APPOINTMENT_STATUSES[apt.status] || APPOINTMENT_STATUSES.pending;
              const profileUrl = studentProfileUrl(apt.student_id);
              const isApptPast = isPast(apt.appointment_date, apt.start_time);
              const initials = (apt.student_name || '')
                .split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');
              const dateLabel = new Date(`${apt.appointment_date}T00:00:00`)
                .toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

              return (
                <VStack spacing={3} align="stretch">

                  {/* Header: estado + código */}
                  <Flex
                    bg="sky.bg" border="1px solid" borderColor="cream.line"
                    borderRadius="14px" px={4} py={3}
                    justify="space-between" align="center"
                  >
                    <HStack spacing={2}>
                      <Pill bg="white" color={apptStatus.color}>{apptStatus.label}</Pill>
                      {isApptPast && apt.status === 'pending' && (
                        <Pill bg="cream.bg2" color="ink.400">Pasada</Pill>
                      )}
                    </HStack>
                    {apt.confirmation_code && (
                      <Tooltip label="Código de confirmación">
                        <Text fontSize="xs" color="sky.ink" fontFamily="mono" fontWeight="700">
                          #{apt.confirmation_code}
                        </Text>
                      </Tooltip>
                    )}
                  </Flex>

                  {/* Alumno */}
                  <Box bg="white" border="1px solid" borderColor="cream.line" borderRadius="14px" p={4}>
                    <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing="0.06em" mb={3}>
                      Alumno
                    </Text>
                    <HStack spacing={3} align="flex-start">
                      <Circle size="40px" bg="coral.500" color="white" fontSize="md" fontWeight="700" flexShrink={0}>
                        {initials || '?'}
                      </Circle>
                      <VStack align="start" spacing={0} flex="1">
                        <Text fontWeight="700" fontSize="md" color="ink.900">
                          {apt.student_name || apt.student_rut}
                        </Text>
                        {apt.student_rut && (
                          <Text fontSize="sm" color="ink.400">RUT: {apt.student_rut}</Text>
                        )}
                      </VStack>
                      {profileUrl && (
                        <Tooltip label="Ver perfil completo del alumno">
                          <Button
                            as="a" href={profileUrl} target="_blank" rel="noopener noreferrer"
                            size="xs" variant="outline" borderColor="coral.500" color="coral.600"
                            _hover={{ bg: 'coral.tint' }}
                            rightIcon={<Icon as={FiExternalLink} />} flexShrink={0}
                          >
                            Ver perfil
                          </Button>
                        </Tooltip>
                      )}
                    </HStack>
                  </Box>

                  {/* Asunto */}
                  {apt.subject && (
                    <Box border="1px solid" borderColor="cream.line" bg="sky.bg" borderRadius="14px" p={4}>
                      <Text fontSize="11px" fontWeight="800" color="sky.ink" textTransform="uppercase" letterSpacing="0.06em" mb={1}>
                        Asunto
                      </Text>
                      <Text fontSize="sm" color="ink.900" fontWeight="500">{apt.subject}</Text>
                    </Box>
                  )}

                  {/* Fecha y hora */}
                  <Box bg="white" border="1px solid" borderColor="cream.line" borderRadius="14px" p={4}>
                    <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing="0.06em" mb={3}>
                      Fecha y hora
                    </Text>
                    <VStack align="start" spacing={2}>
                      <HStack spacing={2}>
                        <Icon as={FiCalendar} color="coral.500" boxSize="14px" />
                        <Text fontSize="sm" fontWeight="500" color="ink.900">{dateLabel}</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Icon as={FiClock} color="coral.500" boxSize="14px" />
                        <Text fontSize="sm" color="ink.900">{apt.start_time?.slice(0, 5)} — {apt.end_time?.slice(0, 5)}</Text>
                      </HStack>
                    </VStack>
                  </Box>

                  {/* Apoderado */}
                  <Box bg="white" border="1px solid" borderColor="cream.line" borderRadius="14px" p={4}>
                    <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing="0.06em" mb={3}>
                      Apoderado
                    </Text>
                    <VStack align="start" spacing={2}>
                      <HStack spacing={2}>
                        <Icon as={FiInfo} color="sky.ink" boxSize="14px" />
                        <Text fontSize="sm" fontWeight="500" color="ink.900">{apt.guardian_name}</Text>
                      </HStack>
                      {apt.guardian_email && (
                        <HStack spacing={2}>
                          <Icon as={FiMail} color="sky.ink" boxSize="14px" />
                          <Text fontSize="sm" color="ink.900">{apt.guardian_email}</Text>
                        </HStack>
                      )}
                      {apt.guardian_phone && (
                        <HStack spacing={2}>
                          <Icon as={FiPhone} color="sky.ink" boxSize="14px" />
                          <Text fontSize="sm" color="ink.900">{apt.guardian_phone}</Text>
                        </HStack>
                      )}
                    </VStack>
                  </Box>

                  {/* Notas del apoderado */}
                  {apt.notes && (
                    <Box
                      border="1px solid" borderColor="cream.line" bg="butter.bg"
                      borderRadius="14px" p={4}
                    >
                      <Text fontSize="11px" fontWeight="800" color="butter.ink" textTransform="uppercase" letterSpacing="0.06em" mb={2}>
                        Notas del apoderado
                      </Text>
                      <Text fontSize="sm" color="ink.900" fontStyle="italic">
                        "{apt.notes}"
                      </Text>
                    </Box>
                  )}

                  {/* Notas del profesor */}
                  {apt.teacher_notes && (
                    <Box
                      border="1px solid" borderColor="cream.line" bg="lav.bg"
                      borderRadius="14px" p={4}
                    >
                      <Text fontSize="11px" fontWeight="800" color="lav.ink" textTransform="uppercase" letterSpacing="0.06em" mb={2}>
                        Notas del profesor
                      </Text>
                      <Text fontSize="sm" color="ink.900" fontStyle="italic">
                        "{apt.teacher_notes}"
                      </Text>
                    </Box>
                  )}

                  {/* Metadata del registro */}
                  <Box bg="white" border="1px solid" borderColor="cream.line" borderRadius="14px" p={4}>
                    <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing="0.06em" mb={3}>
                      Información del registro
                    </Text>
                    <VStack align="start" spacing={2}>
                      {apt.teacher_name && (
                        <HStack spacing={2}>
                          <Icon as={FiInfo} color="ink.400" boxSize="13px" />
                          <Text fontSize="sm" color="ink.700">
                            <Text as="span" fontWeight="600">Profesor:</Text> {apt.teacher_name}
                          </Text>
                        </HStack>
                      )}
                      {apt.confirmation_code && (
                        <HStack spacing={2}>
                          <Icon as={FiCheck} color="coral.500" boxSize="13px" />
                          <Text fontSize="sm" color="ink.700">
                            <Text as="span" fontWeight="600">Código:</Text>{' '}
                            <Text as="span" fontFamily="mono" fontWeight="700" color="coral.700">
                              #{apt.confirmation_code}
                            </Text>
                          </Text>
                        </HStack>
                      )}
                      {apt.created_at && (
                        <HStack spacing={2}>
                          <Icon as={FiClock} color="ink.400" boxSize="13px" />
                          <Text fontSize="sm" color="ink.700">
                            <Text as="span" fontWeight="600">Registrada:</Text> {formatCreatedAt(apt.created_at)}
                          </Text>
                        </HStack>
                      )}
                      {apt.updated_at && apt.updated_at !== apt.created_at && (
                        <HStack spacing={2}>
                          <Icon as={FiEdit3} color="ink.400" boxSize="13px" />
                          <Text fontSize="sm" color="ink.700">
                            <Text as="span" fontWeight="600">Última actualización:</Text> {formatCreatedAt(apt.updated_at)}
                          </Text>
                        </HStack>
                      )}
                      <HStack spacing={2}>
                        <Icon as={FiInfo} color="ink.400" boxSize="13px" />
                        <Text fontSize="sm" color="ink.700">
                          <Text as="span" fontWeight="600">ID interno:</Text> #{apt.id}
                        </Text>
                      </HStack>
                    </VStack>
                  </Box>

                  {/* Campo según acción */}
                  {modalAction === 'cancel' && (
                    <FormControl>
                      <FormLabel fontSize="sm" color="ink.700">Motivo de cancelación</FormLabel>
                      <Textarea
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        placeholder="Explique brevemente el motivo de la cancelación..."
                        rows={3}
                        size="sm"
                        bg="white"
                        focusBorderColor="danger.500"
                      />
                    </FormControl>
                  )}

                  {(modalAction === 'confirm' || modalAction === 'complete') && (
                    <FormControl>
                      <FormLabel fontSize="sm" color="ink.700">
                        Notas del profesor {modalAction === 'complete' ? '(resumen de la reunión)' : '(opcional)'}
                      </FormLabel>
                      <Textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder={
                          modalAction === 'complete'
                            ? 'Resuma los temas tratados y acuerdos alcanzados...'
                            : 'Agregue notas o instrucciones especiales...'
                        }
                        rows={3}
                        size="sm"
                        bg="white"
                        focusBorderColor="coral.500"
                      />
                    </FormControl>
                  )}

                </VStack>
              );
            })()}
          </ModalBody>

          <ModalFooter borderTop="1px solid" borderColor="cream.line">
            <ButtonGroup spacing={3}>
              <Button variant="outline" borderColor="cream.line2" color="ink.700" onClick={closeModal}>
                {modalAction === 'view' ? 'Cerrar' : 'Cancelar'}
              </Button>

              {modalAction !== 'view' && (
                <Button
                  bg={modalAction === 'cancel' ? 'danger.500' : 'coral.500'}
                  color="white"
                  _hover={{ opacity: 0.9 }}
                  onClick={executeAction}
                  isLoading={loading}
                >
                  {modalAction === 'confirm' && 'Confirmar Cita'}
                  {modalAction === 'complete' && 'Marcar Completada'}
                  {modalAction === 'cancel' && 'Cancelar Cita'}
                </Button>
              )}
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}

export default AppointmentsList;
